/**
 * Properties: list, add/edit, and the property hub (overview, financials, tenancies,
 * money, operations, documents, activity and ownership).
 */
import { labelOf, PROPERTY_STATUSES, PROPERTY_TYPES, RENT_FREQUENCIES } from "@shared/constants.ts";
import type { Summary } from "@shared/finance/ledger.ts";
import { ownershipSchema, propertySchema } from "@shared/schemas/portfolio.ts";
import { Button, Card, Col, Dropdown, Nav, Row, Table } from "react-bootstrap";
import { useFieldArray } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router";
import { DocumentList, type DocumentRow, UploadControl } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { ChartPanel, SummaryCards } from "../../components/Financials.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  type AnyForm,
  DateField,
  MoneyField,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { Timeline } from "../../components/Timeline.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { DateRangePicker } from "../../components/ui/DateRangePicker.tsx";
import { FilterBar, FilterSelect, SearchInput } from "../../components/ui/Filters.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatCard,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api, type Params, withQuery } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { percent, useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useDateRange, useSortState, useUrlState } from "../../lib/url-state.ts";
import { ExpenseTable } from "../money/ExpensePages.tsx";
import { LoanTable } from "../money/FinancePages.tsx";
import { IncomeTable } from "../money/IncomePages.tsx";
import { CommunicationTable } from "../operations/CommunicationPages.tsx";
import { ComplianceTable } from "../operations/CompliancePages.tsx";
import { DocumentTable } from "../operations/DocumentPages.tsx";
import { MaintenanceTable } from "../operations/MaintenancePages.tsx";
import { TaskTable } from "../operations/TaskPages.tsx";
import { TenancyTable } from "../tenancies/TenancyPages.tsx";

interface PropertyRow {
  id: string;
  reference: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  town: string;
  county: string;
  postcode: string;
  propertyType: string;
  status: string;
  bedrooms: number | null;
  bathrooms: number | null;
  purchaseDate: string | null;
  purchasePricePence: number | null;
  purchaseCostsPence: number | null;
  currentValuePence: number | null;
  saleDate: string | null;
  salePricePence: number | null;
  saleCostsPence: number | null;
  description: string;
  notes: string;
  occupied: boolean;
  currentTenancyId: string | null;
  currentRentPence: number | null;
  currentRentFrequency: string | null;
  tenantNames: string | null;
  ownerNames: string | null;
}

export function PropertyListPage() {
  const f = useFormat();
  const { get } = useUrlState();
  const { sort, onSort } = useSortState("address");
  const lookups = useLookups();
  const filters: Params = {
    q: get("q"),
    status: get("status"),
    occupancy: get("occupancy"),
    propertyType: get("propertyType"),
    ownerId: get("ownerId"),
  };
  const q = useApi<PropertyRow[]>(
    "/api/properties",
    { ...filters, sort: sort.sort, dir: sort.dir },
    { keepPrevious: true },
  );
  const columns: Column<PropertyRow>[] = [
    {
      key: "address",
      header: "Property",
      sort: "address",
      cell: (p) => (
        <>
          <span className="fw-semibold">{p.addressLine1}</span>
          {p.reference && <span className="text-muted small"> · {p.reference}</span>}
          <div className="small text-muted">
            {p.town} {p.postcode}
          </div>
        </>
      ),
    },
    {
      key: "type",
      header: "Type",
      sort: "type",
      cell: (p) => (
        <>
          {f.label(PROPERTY_TYPES, p.propertyType)}
          {p.bedrooms !== null && <div className="small text-muted">{p.bedrooms} bed</div>}
        </>
      ),
    },
    {
      key: "occupancy",
      header: "Occupancy",
      cell: (p) => (
        <>
          <StatusBadge status={p.occupied ? "occupied" : "vacant"} />
          {p.tenantNames && (
            <div className="small text-muted text-truncate" style={{ maxWidth: 200 }}>
              {p.tenantNames}
            </div>
          )}
        </>
      ),
    },
    {
      key: "rent",
      header: "Current rent",
      sort: "rent",
      align: "end",
      cell: (p) =>
        p.currentRentPence === null ? (
          "—"
        ) : (
          <>
            <Money pence={p.currentRentPence} />
            <div className="small text-muted">{f.label(RENT_FREQUENCIES, p.currentRentFrequency)}</div>
          </>
        ),
    },
    { key: "owners", header: "Owners", cell: (p) => <span className="small">{p.ownerNames ?? "—"}</span> },
    {
      key: "status",
      header: "Status",
      sort: "status",
      cell: (p) => <StatusBadge status={p.status} label={f.label(PROPERTY_STATUSES, p.status)} />,
    },
  ];
  return (
    <>
      <PageHeader
        title="Properties"
        subtitle="Your portfolio"
        actions={
          <>
            <a className="btn btn-outline-secondary" href={withQuery("/api/properties/export.csv", filters)}>
              <Icon name="download" size={16} /> Export CSV
            </a>
            <Can permission="properties:write">
              <IconButtonLink to="/properties/new" icon="plus">
                Add property
              </IconButtonLink>
            </Can>
          </>
        }
      />
      <Card>
        <FilterBar>
          <SearchInput placeholder="Search address, postcode, reference" />
          <FilterSelect
            param="occupancy"
            label="Occupancy"
            options={[
              { value: "occupied", label: "Occupied" },
              { value: "vacant", label: "Vacant" },
            ]}
          />
          <FilterSelect param="status" label="Status" options={PROPERTY_STATUSES} />
          <FilterSelect param="propertyType" label="Type" options={PROPERTY_TYPES} />
          <FilterSelect param="ownerId" label="Owner" options={toOptions(lookups?.owners)} />
        </FilterBar>
        <QueryState query={q}>
          {(rows) => (
            <DataTable
              rows={rows}
              columns={columns}
              rowKey={(p) => p.id}
              rowLink={(p) => `/properties/${p.id}`}
              sort={sort}
              onSort={onSort}
              empty={
                <EmptyState
                  icon="home"
                  title={
                    Object.values(filters).some(Boolean)
                      ? "No properties match these filters"
                      : "Add your first property"
                  }
                  action={
                    <IconButtonLink to="/properties/new" icon="plus">
                      Add property
                    </IconButtonLink>
                  }
                >
                  Properties are the centre of everything: tenancies, rent, income, expenses, compliance and
                  documents all hang off them.
                </EmptyState>
              }
            />
          )}
        </QueryState>
      </Card>
    </>
  );
}

export function PropertyFormPage() {
  const { id } = useParams();
  return (
    <EntityForm
      schema={propertySchema}
      resource="/api/properties"
      id={id}
      title={id ? "Edit property" : "Add property"}
      breadcrumbs={[{ label: "Properties", to: "/properties" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        reference: "",
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        town: "",
        county: "",
        postcode: "",
        country: "GB",
        propertyType: "house",
        status: "active",
        bedrooms: "",
        bathrooms: "",
        purchaseDate: "",
        purchasePricePence: "",
        purchaseCostsPence: "",
        currentValuePence: "",
        saleDate: "",
        salePricePence: "",
        saleCostsPence: "",
        description: "",
        notes: "",
      }}
      toForm={(r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v]))}
      redirect={(s) => `/properties/${s.id}`}
      cancelTo={id ? `/properties/${id}` : "/properties"}
    >
      {(form) => (
        <>
          <h2 className="h6 text-muted text-uppercase">Address</h2>
          <Row>
            <Col md={8}>
              <TextField name="addressLine1" label="Address line 1" autoComplete="address-line1" required />
            </Col>
            <Col md={4}>
              <TextField name="reference" label="Nickname / reference" placeholder="e.g. Flat 2" />
            </Col>
            <Col md={6}>
              <TextField name="addressLine2" label="Address line 2" autoComplete="address-line2" />
            </Col>
            <Col md={6}>
              <TextField name="addressLine3" label="Address line 3" autoComplete="address-line3" />
            </Col>
            <Col md={5}>
              <TextField name="town" label="Town / city" autoComplete="address-level2" required />
            </Col>
            <Col md={4}>
              <TextField name="county" label="County" autoComplete="address-level1" />
            </Col>
            <Col md={3}>
              <TextField name="postcode" label="Postcode" autoComplete="postal-code" required />
            </Col>
          </Row>
          <h2 className="h6 text-muted text-uppercase mt-2">Property</h2>
          <Row>
            <Col md={4}>
              <SelectField name="propertyType" label="Property type" options={PROPERTY_TYPES} required />
            </Col>
            <Col md={4}>
              <SelectField name="status" label="Status" options={PROPERTY_STATUSES} />
            </Col>
            <Col md={2}>
              <NumberField name="bedrooms" label="Bedrooms" min={0} />
            </Col>
            <Col md={2}>
              <NumberField name="bathrooms" label="Bathrooms" min={0} />
            </Col>
            <Col md={12}>
              <TextAreaField name="description" label="Description" rows={2} />
            </Col>
          </Row>
          <h2 className="h6 text-muted text-uppercase mt-2">Acquisition & value</h2>
          <Row>
            <Col md={3}>
              <DateField name="purchaseDate" label="Purchase date" />
            </Col>
            <Col md={3}>
              <MoneyField name="purchasePricePence" label="Purchase price" />
            </Col>
            <Col md={3}>
              <MoneyField name="purchaseCostsPence" label="Purchase costs" hint="SDLT, legal fees etc." />
            </Col>
            <Col md={3}>
              <MoneyField name="currentValuePence" label="Current value (estimate)" />
            </Col>
          </Row>
          {(form.watch("status") === "sold" || form.watch("saleDate")) && (
            <>
              <h2 className="h6 text-muted text-uppercase mt-2">Sale</h2>
              <Row>
                <Col md={4}>
                  <DateField name="saleDate" label="Sale date" />
                </Col>
                <Col md={4}>
                  <MoneyField name="salePricePence" label="Sale price" />
                </Col>
                <Col md={4}>
                  <MoneyField name="saleCostsPence" label="Sale costs" />
                </Col>
              </Row>
            </>
          )}
          <TextAreaField name="notes" label="Notes" />
        </>
      )}
    </EntityForm>
  );
}

interface Ownership {
  id: string;
  ownerId: string;
  ownerName: string;
  ownerType: string;
  shareBp: number;
  effectiveFromDate: string;
  effectiveToDate: string | null;
}

function OwnershipTab({ property }: { property: PropertyRow & { ownership: Ownership[] } }) {
  const f = useFormat();
  const lookups = useLookups();
  const { can } = useAuth();
  return (
    <Section
      title="Ownership"
      actions={
        can("owners:write") && (
          <ModalFormButton
            label="Edit ownership"
            size="lg"
            title="Ownership shares"
            schema={ownershipSchema}
            defaults={{
              shares: property.ownership.length
                ? property.ownership.map((o) => ({
                    ownerId: o.ownerId,
                    sharePercent: (o.shareBp / 100).toString(),
                    effectiveFromDate: o.effectiveFromDate,
                    effectiveToDate: o.effectiveToDate ?? "",
                  }))
                : [
                    {
                      ownerId: "",
                      sharePercent: "100",
                      effectiveFromDate: property.purchaseDate ?? new Date().toISOString().slice(0, 10),
                      effectiveToDate: "",
                    },
                  ],
            }}
            onSubmit={(v) => api.put(`/api/properties/${property.id}/ownership`, v)}
          >
            {(form) => <SharesEditor form={form} owners={toOptions(lookups?.owners)} />}
          </ModalFormButton>
        )
      }
      flush
    >
      {property.ownership.length === 0 ? (
        <EmptyState
          icon="owner"
          title="No owners set"
          action={
            <Link to="/owners/new" className="btn btn-sm btn-outline-primary">
              Add an owner
            </Link>
          }
        >
          Set who owns this property (and in what shares) so tax figures can be produced per owner.
        </EmptyState>
      ) : (
        <Table className="mb-0">
          <thead>
            <tr>
              <th>Owner</th>
              <th className="text-end">Share</th>
              <th>From</th>
              <th>To</th>
            </tr>
          </thead>
          <tbody>
            {property.ownership.map((o) => (
              <tr key={o.id}>
                <td>
                  <Link to={`/owners/${o.ownerId}`}>{o.ownerName}</Link>
                </td>
                <td className="text-end">{percent(o.shareBp)}</td>
                <td>{f.date(o.effectiveFromDate)}</td>
                <td>{o.effectiveToDate ? f.date(o.effectiveToDate) : "Present"}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
}

function SharesEditor({ form, owners }: { form: AnyForm; owners: ReturnType<typeof toOptions> }) {
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "shares" });
  const rootError = (form.formState.errors.shares as { message?: string } | undefined)?.message;
  return (
    <>
      <p className="small text-muted">
        Shares in effect on any date must total 100%. To record a change in ownership, end the old shares and
        add new ones from the change date.
      </p>
      {rootError && <div className="alert alert-danger py-2">{rootError}</div>}
      {fields.map((field, i) => (
        <Row key={field.id} className="align-items-end">
          <Col md={4}>
            <SelectField
              name={`shares.${i}.ownerId`}
              label="Owner"
              options={owners}
              placeholder="Choose"
              required
            />
          </Col>
          <Col md={2}>
            <TextField name={`shares.${i}.sharePercent`} label="Share %" required />
          </Col>
          <Col md={2}>
            <DateField name={`shares.${i}.effectiveFromDate`} label="From" required />
          </Col>
          <Col md={3}>
            <DateField name={`shares.${i}.effectiveToDate`} label="To" />
          </Col>
          <Col md={1} className="mb-3">
            {fields.length > 1 && (
              <Button
                variant="link"
                className="text-danger p-0"
                onClick={() => remove(i)}
                aria-label="Remove share"
              >
                <Icon name="trash" size={16} />
              </Button>
            )}
          </Col>
        </Row>
      ))}
      <Button
        size="sm"
        variant="outline-primary"
        onClick={() =>
          append({
            ownerId: "",
            sharePercent: "",
            effectiveFromDate: new Date().toISOString().slice(0, 10),
            effectiveToDate: "",
          })
        }
      >
        <Icon name="plus" size={14} /> Add owner share
      </Button>
    </>
  );
}

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "financials", label: "Financials" },
  { key: "tenancies", label: "Tenancies" },
  { key: "money", label: "Income & expenses" },
  { key: "finance", label: "Mortgages" },
  { key: "tasks", label: "Tasks" },
  { key: "maintenance", label: "Maintenance" },
  { key: "communications", label: "Communications" },
  { key: "compliance", label: "Compliance" },
  { key: "documents", label: "Documents" },
  { key: "activity", label: "Activity" },
  { key: "ownership", label: "Ownership" },
];

function OverviewTab({ p }: { p: PropertyRow }) {
  const f = useFormat();
  const range = useDateRange();
  const scope = { from: range.from, to: range.to, propertyId: p.id };
  const summary = useApi<{ summary: Summary }>("/api/reports/summary", scope);
  const tenancy = useApi<{
    tenants: Array<{ id: string; name: string; phone: string; email: string }>;
    currentRent: { amountPence: number; frequency: string } | null;
    arrears: { outstandingPence: number } | null;
    startDate: string;
    fixedTermEndDate: string | null;
  }>(p.currentTenancyId ? `/api/tenancies/${p.currentTenancyId}` : null);
  const docs = useApi<DocumentRow[]>("/api/documents", { propertyId: p.id, pageSize: 5 });
  return (
    <Row>
      <Col xl={8}>
        <div className="d-flex justify-content-end mb-2">
          <DateRangePicker range={range} />
        </div>
        <QueryState query={summary}>
          {(d) => (
            <SummaryCards
              summary={d.summary}
              scope={scope}
              extra={
                <Col sm={6} xl={3}>
                  <StatCard
                    label="Outstanding rent"
                    value={<Money pence={tenancy.data?.arrears?.outstandingPence ?? 0} kind="outstanding" />}
                    tone="warning"
                  />
                </Col>
              }
            />
          )}
        </QueryState>
        <Section
          title="Current tenancy"
          actions={
            p.currentTenancyId && (
              <Link to={`/tenancies/${p.currentTenancyId}`} className="btn btn-sm btn-outline-primary">
                View tenancy
              </Link>
            )
          }
        >
          {!p.currentTenancyId ? (
            <EmptyState
              icon="key"
              title="Vacant"
              action={
                <IconButtonLink to={`/tenancies/new?propertyId=${p.id}`} icon="plus">
                  Add tenancy
                </IconButtonLink>
              }
            >
              This property has no current tenancy.
            </EmptyState>
          ) : (
            <QueryState query={tenancy}>
              {(t) => (
                <DefinitionList
                  items={[
                    ["Tenants", t.tenants.map((x) => x.name).join(", ")],
                    [
                      "Current rent",
                      t.currentRent
                        ? `${f.money(t.currentRent.amountPence)} ${f.label(RENT_FREQUENCIES, t.currentRent.frequency).toLowerCase()}`
                        : "Not set",
                    ],
                    ["Started", f.date(t.startDate)],
                    ["Fixed term ends", f.date(t.fixedTermEndDate)],
                    [
                      "Contact",
                      t.tenants
                        .map((x) => x.phone || x.email)
                        .filter(Boolean)
                        .join(", "),
                    ],
                  ]}
                />
              )}
            </QueryState>
          )}
        </Section>
        <Section
          title="Open tasks"
          flush
          actions={
            <Link to={`/tasks/new?propertyId=${p.id}`} className="btn btn-sm btn-outline-primary">
              Add task
            </Link>
          }
        >
          <TaskTable filters={{ propertyId: p.id, view: "open", pageSize: 5 }} compact />
        </Section>
        <Section title="Upcoming compliance" flush>
          <ComplianceTable filters={{ propertyId: p.id }} compact />
        </Section>
        <Section title="Open maintenance" flush>
          <MaintenanceTable filters={{ propertyId: p.id, status: "open" }} compact />
        </Section>
      </Col>
      <Col xl={4}>
        <Section title="Property details">
          <DefinitionList
            items={[
              [
                "Type",
                `${f.label(PROPERTY_TYPES, p.propertyType)}${p.bedrooms !== null ? ` · ${p.bedrooms} bed` : ""}${p.bathrooms !== null ? ` · ${p.bathrooms} bath` : ""}`,
              ],
              ["Owners", p.ownerNames],
              [
                "Purchased",
                p.purchaseDate ? `${f.date(p.purchaseDate)} for ${f.money(p.purchasePricePence)}` : "—",
              ],
              ["Current value", f.money(p.currentValuePence)],
              p.saleDate ? ["Sold", `${f.date(p.saleDate)} for ${f.money(p.salePricePence)}`] : null,
            ]}
          />
          {p.description && <p className="small mb-0">{p.description}</p>}
        </Section>
        <Section title="Recent documents">
          <DocumentList docs={(docs.data ?? []).slice(0, 5)} />
        </Section>
        <Section title="Recent activity">
          <Timeline filters={{ propertyId: p.id }} limit={8} showFilters={false} showProperty={false} />
        </Section>
      </Col>
    </Row>
  );
}

function FinancialsTab({ p }: { p: PropertyRow }) {
  const range = useDateRange();
  const scope = { from: range.from, to: range.to, propertyId: p.id };
  const pnl = useApi<{ summary: Summary }>("/api/reports/summary", scope);
  const expenses = useApi<{ byCategory: Array<{ key: string; label: string; summary: Summary }> }>(
    "/api/reports/expenses",
    scope,
  );
  return (
    <>
      <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
        <DateRangePicker range={range} />
        <Link className="btn btn-sm btn-outline-primary" to={withQuery("/reports/transactions", scope)}>
          View all transactions
        </Link>
      </div>
      <QueryState query={pnl}>{(d) => <SummaryCards summary={d.summary} scope={scope} />}</QueryState>
      <ChartPanel scope={scope} />
      <Section title="Expenses by category" flush>
        <QueryState query={expenses}>
          {(d) => (
            <Table className="mb-0">
              <tbody>
                {d.byCategory.map((c) => (
                  <tr key={c.key}>
                    <td>
                      <Link
                        to={withQuery("/reports/transactions", { ...scope, kind: "expense", typeKey: c.key })}
                      >
                        {c.label}
                      </Link>
                    </td>
                    <td className="text-end">
                      <Money
                        pence={c.summary.expensesPence + c.summary.capitalPence + c.summary.nonAllowablePence}
                        kind="expense"
                      />
                    </td>
                  </tr>
                ))}
                {d.byCategory.length === 0 && (
                  <tr>
                    <td className="text-muted small">No expenses in this period.</td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </QueryState>
      </Section>
    </>
  );
}

function MoneyTab({ p }: { p: PropertyRow }) {
  const range = useDateRange();
  const scope = { from: range.from, to: range.to, propertyId: p.id };
  return (
    <>
      <div className="d-flex flex-wrap justify-content-between gap-2 mb-3">
        <DateRangePicker range={range} />
        <div className="d-flex gap-2">
          <Can permission="income:write">
            <IconButtonLink to={`/income/new?propertyId=${p.id}`} icon="plus">
              Record income
            </IconButtonLink>
          </Can>
          <Can permission="expenses:write">
            <IconButtonLink to={`/expenses/new?propertyId=${p.id}`} icon="plus" variant="outline-primary">
              Add expense
            </IconButtonLink>
          </Can>
        </div>
      </div>
      <Section title="Income" flush>
        <IncomeTable filters={scope} showProperty={false} />
      </Section>
      <Section title="Expenses" flush>
        <ExpenseTable filters={scope} showProperty={false} />
      </Section>
    </>
  );
}

export function PropertyOverviewPage() {
  const { id } = useParams();
  const { get, set } = useUrlState();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const { can } = useAuth();
  const tab = get("tab") || "overview";
  const q = useApi<PropertyRow & { ownership: Ownership[] }>(`/api/properties/${id}`);
  return (
    <QueryState query={q}>
      {(p) => (
        <>
          <PageHeader
            title={p.addressLine1}
            subtitle={
              <>
                {[p.addressLine2, p.town, p.postcode].filter(Boolean).join(", ")} ·{" "}
                <StatusBadge status={p.occupied ? "occupied" : "vacant"} />{" "}
                <StatusBadge status={p.status} label={labelOf(PROPERTY_STATUSES, p.status)} />
              </>
            }
            breadcrumbs={[{ label: "Properties", to: "/properties" }, { label: p.addressLine1 }]}
            actions={
              <>
                <Can permission="income:write">
                  <IconButtonLink
                    to={`/income/new?propertyId=${p.id}${p.currentTenancyId ? `&tenancyId=${p.currentTenancyId}` : ""}`}
                    icon="plus"
                  >
                    Income
                  </IconButtonLink>
                </Can>
                <Can permission="expenses:write">
                  <IconButtonLink
                    to={`/expenses/new?propertyId=${p.id}`}
                    icon="plus"
                    variant="outline-primary"
                  >
                    Expense
                  </IconButtonLink>
                </Can>
                <Dropdown align="end">
                  <Dropdown.Toggle variant="outline-secondary">More</Dropdown.Toggle>
                  <Dropdown.Menu className="shadow">
                    {can("tenancies:write") && (
                      <Dropdown.Item as={Link} to={`/tenancies/new?propertyId=${p.id}`}>
                        Add tenancy
                      </Dropdown.Item>
                    )}
                    {can("tasks:write") && (
                      <Dropdown.Item as={Link} to={`/tasks/new?propertyId=${p.id}`}>
                        Add task
                      </Dropdown.Item>
                    )}
                    {can("maintenance:write") && (
                      <Dropdown.Item as={Link} to={`/maintenance/new?propertyId=${p.id}`}>
                        Report maintenance
                      </Dropdown.Item>
                    )}
                    {can("communications:write") && (
                      <Dropdown.Item as={Link} to={`/communications/new?propertyId=${p.id}`}>
                        Log communication
                      </Dropdown.Item>
                    )}
                    {can("compliance:write") && (
                      <Dropdown.Item as={Link} to={`/compliance/new?propertyId=${p.id}`}>
                        Add compliance record
                      </Dropdown.Item>
                    )}
                    {can("finance:write") && (
                      <Dropdown.Item as={Link} to={`/finance/new?propertyId=${p.id}`}>
                        Add mortgage
                      </Dropdown.Item>
                    )}
                    {can("properties:write") && (
                      <Dropdown.Item as={Link} to={`/properties/${p.id}/edit`}>
                        Edit property
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </>
            }
          />
          <Nav
            variant="tabs"
            className="nav-tabs-scroll mb-4"
            activeKey={tab}
            onSelect={(k) => set({ tab: k === "overview" ? null : k }, { resetPage: true })}
          >
            {TABS.map((t) => (
              <Nav.Item key={t.key}>
                <Nav.Link eventKey={t.key} href={`?tab=${t.key}`} onClick={(e) => e.preventDefault()}>
                  {t.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
          {tab === "overview" && <OverviewTab p={p} />}
          {tab === "financials" && <FinancialsTab p={p} />}
          {tab === "tenancies" && (
            <Section
              title="Tenancies (current and historical)"
              flush
              actions={
                <Can permission="tenancies:write">
                  <IconButtonLink to={`/tenancies/new?propertyId=${p.id}`} icon="plus">
                    Add tenancy
                  </IconButtonLink>
                </Can>
              }
            >
              <TenancyTable filters={{ propertyId: p.id }} showProperty={false} />
            </Section>
          )}
          {tab === "money" && <MoneyTab p={p} />}
          {tab === "finance" && (
            <Section
              title="Mortgages & loans"
              flush
              actions={
                <Can permission="finance:write">
                  <IconButtonLink to={`/finance/new?propertyId=${p.id}`} icon="plus">
                    Add mortgage
                  </IconButtonLink>
                </Can>
              }
            >
              <LoanTable propertyId={p.id} />
            </Section>
          )}
          {tab === "tasks" && (
            <Section
              title="Tasks"
              flush
              actions={
                <Can permission="tasks:write">
                  <IconButtonLink to={`/tasks/new?propertyId=${p.id}`} icon="plus">
                    Add task
                  </IconButtonLink>
                </Can>
              }
            >
              <TaskTable filters={{ propertyId: p.id, view: "all" }} />
            </Section>
          )}
          {tab === "maintenance" && (
            <Section
              title="Maintenance"
              flush
              actions={
                <Can permission="maintenance:write">
                  <IconButtonLink to={`/maintenance/new?propertyId=${p.id}`} icon="plus">
                    Report issue
                  </IconButtonLink>
                </Can>
              }
            >
              <MaintenanceTable filters={{ propertyId: p.id }} />
            </Section>
          )}
          {tab === "communications" && (
            <Section
              title="Communications"
              flush
              actions={
                <Can permission="communications:write">
                  <IconButtonLink to={`/communications/new?propertyId=${p.id}`} icon="plus">
                    Log communication
                  </IconButtonLink>
                </Can>
              }
            >
              <CommunicationTable filters={{ propertyId: p.id }} />
            </Section>
          )}
          {tab === "compliance" && (
            <Section
              title="Compliance"
              flush
              actions={
                <Can permission="compliance:write">
                  <IconButtonLink to={`/compliance/new?propertyId=${p.id}`} icon="plus">
                    Add record
                  </IconButtonLink>
                </Can>
              }
            >
              <ComplianceTable filters={{ propertyId: p.id }} />
            </Section>
          )}
          {tab === "documents" && (
            <Section title="All documents for this property" flush>
              {can("documents:write") && (
                <div className="p-3 border-bottom">
                  <UploadControl entityType="property" entityId={p.id} />
                </div>
              )}
              <DocumentTable filters={{ propertyId: p.id }} />
            </Section>
          )}
          {tab === "activity" && (
            <Section title="Activity">
              <Timeline filters={{ propertyId: p.id }} showProperty={false} />
            </Section>
          )}
          {tab === "ownership" && (
            <>
              <OwnershipTab property={p} />
              <AuditHistory entityType="property" entityId={p.id} />
              <Can permission="properties:delete">
                <div className="text-end">
                  <ConfirmButton
                    title="Delete this property?"
                    body="Only properties without tenancies or financial records can be deleted. Otherwise set the status to Archived."
                    onConfirm={async () => {
                      await api.delete(`/api/properties/${p.id}`);
                      await invalidate();
                      navigate("/properties");
                    }}
                  >
                    Delete property
                  </ConfirmButton>
                </div>
              </Can>
            </>
          )}
        </>
      )}
    </QueryState>
  );
}
